"use client";

import React from "react";

interface ProductDescriptionProps {
  description: string;
  expanded: boolean;
  onToggle: () => void;
}

type ContentBlock =
  | { type: "paragraph"; text: string }
  | { type: "feature"; items: Array<{ emoji: string; text: string }> }
  | { type: "notes"; items: Array<{ label: string; notes: string[] }> };

const NOTE_RE = /^(top|heart|base|middle|opening)\s+notes?\s*:\s*(.+)$/i;
const FRAGRANCE_HEADER_RE = /fragrance\s+notes?/i;

function isEmojiLine(line: string): boolean {
  return (line.codePointAt(0) ?? 0) > 127;
}

function splitEmoji(line: string): { emoji: string; text: string } {
  const spaceIdx = line.search(/\s/);
  if (spaceIdx < 1) return { emoji: "", text: line };
  return { emoji: line.slice(0, spaceIdx), text: line.slice(spaceIdx).trim() };
}

function parseDescription(raw: string): ContentBlock[] {
  const lines = raw.split("\n").map(l => l.trim()).filter(Boolean);
  const blocks: ContentBlock[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fragrance section header (handles "🌿 Fragrance Notes" format)
    if (FRAGRANCE_HEADER_RE.test(line)) {
      i++;
      const items: Array<{ label: string; notes: string[] }> = [];
      while (i < lines.length && NOTE_RE.test(lines[i])) {
        const m = lines[i].match(NOTE_RE)!;
        items.push({ label: m[1], notes: m[2].split("•").map(n => n.trim()).filter(Boolean) });
        i++;
      }
      if (items.length > 0) blocks.push({ type: "notes", items });
      continue;
    }

    // Standalone note lines (Top Notes: / Heart Notes: etc. without a header)
    if (NOTE_RE.test(line)) {
      const items: Array<{ label: string; notes: string[] }> = [];
      while (i < lines.length && NOTE_RE.test(lines[i])) {
        const m = lines[i].match(NOTE_RE)!;
        items.push({ label: m[1], notes: m[2].split("•").map(n => n.trim()).filter(Boolean) });
        i++;
      }
      blocks.push({ type: "notes", items });
      continue;
    }

    // Emoji-prefixed feature lines
    if (isEmojiLine(line)) {
      const items: Array<{ emoji: string; text: string }> = [];
      while (
        i < lines.length &&
        isEmojiLine(lines[i]) &&
        !FRAGRANCE_HEADER_RE.test(lines[i]) &&
        !NOTE_RE.test(lines[i])
      ) {
        items.push(splitEmoji(lines[i]));
        i++;
      }
      if (items.length > 0) blocks.push({ type: "feature", items });
      continue;
    }

    // Plain prose paragraph
    blocks.push({ type: "paragraph", text: line });
    i++;
  }

  return blocks;
}

export const ProductDescription: React.FC<ProductDescriptionProps> = ({
  description,
  expanded,
  onToggle,
}) => {
  const blocks = parseDescription(description);

  return (
    <div className="overflow-hidden rounded-2xl border border-[#EDE9E1]" style={{ background: "#FDFAF6" }}>
      <div className={`relative overflow-hidden transition-all duration-300 ${!expanded ? "max-h-[220px]" : ""}`}>
        <div className="divide-y divide-[#EDE9E1]">
          {blocks.map((block, idx) => {
            if (block.type === "paragraph") {
              return (
                <div key={idx} className="px-5 py-4">
                  <p className="text-[13.5px] leading-[1.85] text-[#4A4A4A]">{block.text}</p>
                </div>
              );
            }

            if (block.type === "feature") {
              return (
                <div key={idx} className="px-5 py-4">
                  <ul className="grid grid-cols-1 gap-y-2.5 sm:grid-cols-2">
                    {block.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2.5">
                        <span className="shrink-0 text-base leading-none mt-[1px]" aria-hidden="true">
                          {item.emoji}
                        </span>
                        <span className="text-[13px] leading-[1.6] text-[#1D1D1D]">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }

            if (block.type === "notes") {
              return (
                <div key={idx} className="px-5 py-4">
                  <div className="mb-3 flex items-center gap-2">
                    <span className="h-px flex-1 bg-[#E8E3DA]" />
                    <span className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-[#8A8A85]">
                      Fragrance Notes
                    </span>
                    <span className="h-px flex-1 bg-[#E8E3DA]" />
                  </div>
                  <div className="space-y-2.5">
                    {block.items.map((row, j) => (
                      <div key={j} className="flex items-start gap-3">
                        <span className="w-[60px] shrink-0 text-[11px] font-semibold uppercase tracking-wide text-[#8A8A85] pt-0.5">
                          {row.label}
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {row.notes.map((note, k) => (
                            <span
                              key={k}
                              className="rounded-full px-2.5 py-0.5 text-[11.5px] font-medium"
                              style={{ background: "#FEF0EC", color: "#D94A39" }}
                            >
                              {note}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            return null;
          })}
        </div>

        {!expanded && (
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#FDFAF6] to-transparent" />
        )}
      </div>

      <div className="border-t border-[#EDE9E1] px-5 py-2.5">
        <button
          onClick={onToggle}
          className="flex items-center gap-1.5 text-[12.5px] font-semibold transition"
          style={{ color: "#F26250" }}
        >
          {expanded ? "Show less" : "Read more"}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            {expanded
              ? <polyline points="18 15 12 9 6 15" />
              : <polyline points="6 9 12 15 18 9" />}
          </svg>
        </button>
      </div>
    </div>
  );
};
